import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';
import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import axiosClient from './axiosClient';

interface AxiosBaseQueryConfig {
  baseUrl: string;
  baseOptions?: AxiosRequestConfig;
}

interface AxiosBaseQueryArgs {
  url: string;
  method: AxiosRequestConfig['method'];
  data?: unknown;
  params?: unknown;
  queryOptions?: AxiosRequestConfig;
}

// Define error type for the custom base query
interface CustomError {
  status?: number;
  data?: unknown;
}

const axiosBaseQuery =
  ({
    baseUrl,
    baseOptions = {},
  }: AxiosBaseQueryConfig): BaseQueryFn<
    AxiosBaseQueryArgs,
    unknown,
    CustomError
  > =>
  async ({ url, method, data, params, queryOptions = {} }, { signal }) => {
    try {
      const result: AxiosResponse = await axiosClient({
        url: new URL(url, baseUrl).toString(),
        method,
        data,
        params,
        signal,
        ...baseOptions,
        ...queryOptions,
      });
      return { data: result.data };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return {
          error: {
            status: 499,
            data: 'Request was cancelled',
          },
        };
      }

      return {
        error: {
          status: err.response?.status,
          data: err.response?.data ?? err.message,
        },
      };
    }
  };

const apiSlice = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
  }),
  endpoints: () => ({}),
});

export default apiSlice;
